/**
 * signedFlag — the ONE shared implementation of the parent-gated,
 * tamper-RESISTANT client feature-flag scheme (grand-review P1-15 dedup).
 *
 * Six modules used to hand-copy an identical "signed client feature-flag"
 * scheme — a localStorage flag a child (or a stray script) cannot self-enable.
 * They MUST stay byte-compatible with each other AND with the canonical writer/
 * reader web-child/src/flagGuard.ts: an envelope written by one module must
 * verify in another, and `window.__lwaRigorFlags` must stay interoperable.
 * `createSignedFlag(flagKey)` reproduces that scheme BYTE-FOR-BYTE so every
 * caller can be a thin wrapper over one audited copy:
 *   • same gate-token key `lwa_flag_gate_v1`
 *   • same FNV-1a 32-bit hash
 *   • same signature `fnv1a(`${flagKey}|${value}|${token}`)` (key|value|token)
 *   • same envelope `{ v: 'on' | 'off', sig }`
 *   • same default handling (absent/bare/forged → the flag's default)
 *   • same parent-JWT write gate, never-throws reads, best-effort blocked storage
 * so an envelope written by any factory instance is accepted by flagGuard's
 * `readGuardedFlag` (and vice-versa) for a flagGuard-registered key.
 *
 * ⚠️ SECURITY — the gate is NEVER weakened here: DEFAULT stays at the caller's
 * declared default, a parent JWT is required to write a non-default value, and
 * every bare / legacy / foreign / wrong-signature / token-less write is IGNORED
 * (reads the default). A child can neither enable nor disable a parent-owned
 * flag with one localStorage write.
 *
 * HONESTY NOTE (same as flagGuard): this is tamper-RESISTANCE, not cryptography
 * — the token and hash both live on the device, so a determined adult with
 * devtools can still forge a signature. The goal is only that a child (or a
 * stray script) cannot flip a parent-controlled flag with one localStorage
 * write. Server-side records are the authoritative gate where one exists.
 */

/** The gate-token key — shared across every signed flag (and flagGuard). */
export const GATE_TOKEN_KEY = 'lwa_flag_gate_v1';

/**
 * FNV-1a 32-bit — tiny, deterministic, dependency-free. MUST stay byte-identical
 * to web-child/flagGuard.ts::fnv1a (an envelope signed here verifies there).
 */
export function fnv1a(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/* ── storage (best-effort; matches the ui-shared copies: null on a blocked read,
 *    silent on a blocked write — the value simply isn't persisted) ── */
function getItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function setItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* blocked storage — best effort */
  }
}
function removeItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* blocked storage — best effort */
  }
}

function mintGateToken(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function getOrCreateGateToken(): string {
  const existing = getItem(GATE_TOKEN_KEY);
  if (existing) return existing;
  const token = mintGateToken();
  setItem(GATE_TOKEN_KEY, token);
  return token;
}

/**
 * True when a PARENT is signed in on this device: a structurally valid,
 * unexpired `auth_token` JWT (the parent-portal session — the same identity
 * flagGuard.hasParentGate rides on). The child token (`child_auth_token`) does
 * NOT qualify. Shared by every signed flag's `hasParentGate` export.
 */
export function hasParentGate(): boolean {
  try {
    const token = getItem('auth_token');
    if (!token) return false;
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const padded = parts[1] + '='.repeat((4 - (parts[1].length % 4)) % 4);
    const claims = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
    if (!claims || typeof claims !== 'object' || !claims.sub) return false;
    return typeof claims.exp === 'number' && claims.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

/** Options for a signed flag: its default reading + optional post-write hook. */
export interface SignedFlagOptions {
  /** The flag's default (and fail-safe) reading when absent/bare/forged. Default false (DEFAULT-OFF). */
  defaultOn?: boolean;
  /** Fired after a successful parent-gated write (both directions), for side-effects
   *  like a re-render event. Best-effort — the flag write already succeeded. */
  onAfterWrite?: (enabled: boolean) => void;
}

/** The primitive a flag module wraps. Symbol names are re-exported per module. */
export interface SignedFlag {
  /** The localStorage key for this flag. */
  readonly key: string;
  /** The flag's default reading (true for DEFAULT-ON flags). */
  readonly defaultOn: boolean;
  /** Read the flag. Never throws. Only a correctly parent-signed envelope can
   *  move it off its default; every other shape reads the default. */
  isEnabled(): boolean;
  /** Parent-gated write. Writing the default clears the key; writing the
   *  non-default stores a signed envelope. Refuses (false, writes nothing)
   *  without a live parent session. */
  setEnabled(enabled: boolean): boolean;
  /** True when a live parent session gates writes on this device. */
  hasParentGate(): boolean;
  /** TEST-ONLY: sign the flag ON without the parent-JWT check. */
  __setOnForTests(): void;
  /** TEST-ONLY: sign the flag OFF without the parent-JWT check. */
  __setOffForTests(): void;
  /** Low-level seam: the exact signature for a value on this device's token. */
  signatureFor(value: 'on' | 'off', token: string): string;
}

/**
 * Build a signed feature flag over `flagKey`, reproducing flagGuard.ts's scheme
 * byte-for-byte. `defaultOn` controls the default (and fail-safe) reading; a
 * bare/forged/absent value always reads that default and can never be moved off
 * it without a correctly parent-signed envelope.
 */
export function createSignedFlag(flagKey: string, options: SignedFlagOptions = {}): SignedFlag {
  const defaultOn = options.defaultOn === true;
  const onAfterWrite = options.onAfterWrite;

  function signatureFor(value: 'on' | 'off', token: string): string {
    return fnv1a(`${flagKey}|${value}|${token}`);
  }

  function isEnabled(): boolean {
    const raw = getItem(flagKey);
    if (raw === null) return defaultOn; // DEFAULT
    try {
      const parsed = JSON.parse(raw) as { v?: unknown; sig?: unknown } | null;
      if (!parsed || typeof parsed !== 'object') return defaultOn; // bare legacy value
      if ((parsed.v !== 'on' && parsed.v !== 'off') || typeof parsed.sig !== 'string') return defaultOn;
      const token = getItem(GATE_TOKEN_KEY);
      if (!token) return defaultOn; // an envelope with no gate token was never parent-set
      if (parsed.sig !== signatureFor(parsed.v, token)) return defaultOn;
      return parsed.v === 'on';
    } catch {
      // Bare strings ('off', '0', 'false', 'on', …) land here: unauthorized —
      // ignored by design (this is the child-device kill switch).
      return defaultOn;
    }
  }

  function writeSigned(value: 'on' | 'off'): void {
    const token = getOrCreateGateToken();
    setItem(flagKey, JSON.stringify({ v: value, sig: signatureFor(value, token) }));
  }

  function setEnabled(enabled: boolean): boolean {
    if (!hasParentGate()) return false;
    if (enabled === defaultOn) {
      // Back to the default → clear the key (nothing to store).
      removeItem(flagKey);
    } else {
      writeSigned(enabled ? 'on' : 'off');
    }
    onAfterWrite?.(enabled);
    return true;
  }

  return {
    key: flagKey,
    defaultOn,
    isEnabled,
    setEnabled,
    hasParentGate,
    signatureFor,
    __setOnForTests: () => writeSigned('on'),
    __setOffForTests: () => writeSigned('off'),
  };
}
